import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  BarChart3,
  Camera,
  CheckCircle2,
  ExternalLink,
  FileCode2,
  FolderOpen,
  GraduationCap,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  WandSparkles,
  X,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import type { AgentSpecRow, BuildJobDescriptionResult, SkillRow } from '@command-room/shared';
import {
  useAgent,
  useAssignSkill,
  useBuildJobDescription,
  useDepartments,
  useImportAgentAvatar,
  useSeedSpecFromXml,
  useSkills,
  useUnassignSkill,
  useUpdateAgent,
  useUpdateAgentSpec,
} from '@/api/queries';
import { useAuth } from '@/hooks/useAuth';
import { Select, SelectItem } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AgentLearningsTab } from '@/components/personnel/AgentLearningsTab';
import { AgentStatsTab } from '@/components/personnel/AgentStatsTab';
import { relativeTime } from '@/lib/format';
import { deriveAgentProfile } from '@/lib/agentProfile';
import { ProfileHeader } from '@/components/agent-profile/ProfileHeader';
import { ProfileTab } from '@/components/agent-profile/ProfileTab';

type EditableSpec = Omit<AgentSpecRow, 'agent_name' | 'updated_by' | 'updated_at' | 'needs_reload'>;

const SPEC_SECTIONS: { key: keyof EditableSpec; label: string; hint: string }[] = [
  { key: 'personality_text', label: 'Personality', hint: 'Character, personality traits, personal background, and personal details — who the agent is as a person.' },
  { key: 'role_text', label: 'Professional role', hint: 'Professional responsibilities, scope, and job duties — what the agent does.' },
  { key: 'nogos_text', label: 'No-gos', hint: 'Hard limits — things the agent must never do.' },
  { key: 'tasks_text', label: 'Tasks', hint: 'Step-by-step task instructions the agent follows.' },
  { key: 'skills_authorities_text', label: 'Skills & Authorities', hint: 'Which tools and permissions the agent may invoke.' },
  { key: 'important_notes_text', label: 'Important notes', hint: 'Edge cases, constraints, and other runtime notes.' },
  { key: 'comms_overrides_text', label: 'Comms overrides', hint: 'Communication style overrides (optional).' },
];

// ─── Avatar edit dialog ───────────────────────────────────────────────────────

function AvatarEditDialog({
  agentName,
  open,
  onOpenChange,
}: {
  agentName: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const importAvatar = useImportAgentAvatar(agentName);
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (open) { setUrl(''); importAvatar.reset(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function onImport() {
    if (!url.trim()) return;
    await importAvatar.mutateAsync(url.trim());
    setTimeout(() => onOpenChange(false), 600);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Import agent portrait" className="max-w-sm">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="icon-url">Image URL</Label>
            <Input
              id="icon-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://drive.google.com/uc?id=…"
              onKeyDown={(e) => e.key === 'Enter' && onImport()}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            The server will fetch and store the image locally.
            For Google Drive: share the file publicly, then use the{' '}
            <code className="bg-muted px-1 rounded">/uc?id=FILE_ID</code> URL.
          </p>
          {importAvatar.isSuccess && (
            <p className="text-sm text-success flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" /> Imported successfully
            </p>
          )}
          {importAvatar.error && (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              {(importAvatar.error as Error).message}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <DialogClose asChild>
              <Button variant="outline" type="button">Cancel</Button>
            </DialogClose>
            <Button onClick={onImport} disabled={!url.trim() || importAvatar.isPending}>
              {importAvatar.isPending ? 'Importing…' : 'Import'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Skill assign dialog ──────────────────────────────────────────────────────

function SkillAssignDialog({
  agentName,
  assignedIds,
  open,
  onOpenChange,
}: {
  agentName: string;
  assignedIds: Set<number>;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { data: skills, isLoading } = useSkills();
  const assignSkill = useAssignSkill();
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) setSearch('');
  }, [open]);

  const query = search.trim().toLowerCase();
  const candidates = (skills ?? []).filter((s: SkillRow) => {
    if (s.is_individual || assignedIds.has(s.id)) return false;
    if (!query) return true;
    return (
      s.skill_name.toLowerCase().includes(query) ||
      s.skill_id.toLowerCase().includes(query) ||
      s.description.toLowerCase().includes(query)
    );
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Add skill" className="max-w-lg">
        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search skills…"
              className="pl-8"
              autoFocus
            />
          </div>

          <div className="flex max-h-96 flex-col gap-1.5 overflow-y-auto">
            {isLoading ? (
              <div className="flex flex-col gap-1.5 py-1">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-11 rounded-md" />
                ))}
              </div>
            ) : candidates.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                No matching skills.
              </p>
            ) : (
              candidates.map((skill) => (
                <div
                  key={skill.id}
                  className="flex items-center gap-3 rounded-md border border-border p-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{skill.skill_name}</p>
                    <p className="truncate text-xs text-muted-foreground">{skill.description}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 shrink-0 px-2 text-xs"
                    disabled={assignSkill.isPending}
                    onClick={() => assignSkill.mutate({ agentName, skillId: skill.skill_id })}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add
                  </Button>
                </div>
              ))
            )}
          </div>

          {assignSkill.error && (
            <p className="flex items-center gap-1 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {(assignSkill.error as Error).message}
            </p>
          )}

          <div className="flex justify-end pt-1">
            <DialogClose asChild>
              <Button variant="outline" type="button">Done</Button>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Job description tab ──────────────────────────────────────────────────────

function JobDescriptionTab({ agentName, hasSpec }: { agentName: string; hasSpec: boolean }) {
  const build = useBuildJobDescription(agentName);
  const [result, setResult] = useState<BuildJobDescriptionResult | null>(null);
  const [writeToFile, setWriteToFile] = useState(false);
  const preRef = useRef<HTMLPreElement>(null);

  async function onBuild() {
    const r = await build.mutateAsync({ write: writeToFile });
    setResult(r);
  }

  if (!hasSpec) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        No spec data — seed from XML first before building the job description.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <input
            id="write-toggle"
            type="checkbox"
            checked={writeToFile}
            onChange={(e) => setWriteToFile(e.target.checked)}
            className="h-4 w-4 rounded border-border"
          />
          <Label htmlFor="write-toggle" className="font-normal cursor-pointer">
            Export to <code className="text-xs bg-muted px-1 rounded">job_description.xml</code> on disk
          </Label>
        </div>
        <Button
          onClick={onBuild}
          disabled={build.isPending}
          className="ml-auto"
        >
          <WandSparkles className="h-4 w-4" />
          {build.isPending ? 'Building…' : 'Build'}
        </Button>
      </div>

      {build.error && (
        <p className="text-sm text-destructive flex items-center gap-1">
          <AlertCircle className="h-4 w-4" />
          {(build.error as Error).message}
        </p>
      )}

      {result && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <CheckCircle2 className="h-3.5 w-3.5 text-success" />
            Built successfully
            {result.written && <span>· exported to job_description.xml</span>}
          </div>
          <pre
            ref={preRef}
            className="max-h-[500px] overflow-auto rounded-lg border border-border bg-muted/40 p-4 text-xs font-mono leading-relaxed whitespace-pre"
          >
            {result.xml}
          </pre>
        </div>
      )}

      {!result && !build.isPending && (
        <div className="py-8 text-center text-sm text-muted-foreground">
          Click Build to assemble the job description from your spec + shared parts.
        </div>
      )}
    </div>
  );
}

// ─── Spec editor ──────────────────────────────────────────────────────────────

function SpecEditor({ agentName, spec }: { agentName: string; spec: AgentSpecRow | null }) {
  const updateSpec = useUpdateAgentSpec(agentName);
  const seedFromXml = useSeedSpecFromXml(agentName);
  const [saved, setSaved] = useState(false);
  const [seedResult, setSeedResult] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<keyof EditableSpec>('role_text');

  const specIsEmpty = !spec || !spec.role_text?.trim();

  const defaultValues: EditableSpec = {
    personality_text: spec?.personality_text ?? '',
    role_text: spec?.role_text ?? '',
    nogos_text: spec?.nogos_text ?? '',
    tasks_text: spec?.tasks_text ?? '',
    skills_authorities_text: spec?.skills_authorities_text ?? '',
    important_notes_text: spec?.important_notes_text ?? '',
    comms_overrides_text: spec?.comms_overrides_text ?? '',
  };

  const { register, handleSubmit, reset, formState: { isDirty, dirtyFields } } = useForm<EditableSpec>({
    defaultValues,
  });

  useEffect(() => {
    if (spec) reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec]);

  async function onSubmit(data: EditableSpec) {
    await updateSpec.mutateAsync({
      ...data,
      comms_overrides_text: data.comms_overrides_text || null,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
    reset(data);
  }

  async function onSeedFromXml() {
    try {
      const r = await seedFromXml.mutateAsync({ force: !specIsEmpty });
      setSeedResult(r.message);
      setTimeout(() => setSeedResult(null), 4000);
    } catch (err) {
      setSeedResult(`Error: ${(err as Error).message}`);
      setTimeout(() => setSeedResult(null), 5000);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Seed from XML button — shown when spec is empty or as override */}
      <div className="flex items-center justify-between border-b border-border pb-3">
        <p className="text-xs text-muted-foreground">
          {specIsEmpty
            ? 'No spec in DB yet — load from the agent\'s agent_spec.xml on disk.'
            : 'Spec loaded from DB.'}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onSeedFromXml}
          disabled={seedFromXml.isPending}
        >
          <FileCode2 className="h-3.5 w-3.5" />
          {seedFromXml.isPending
            ? 'Loading…'
            : specIsEmpty
            ? 'Load from XML'
            : 'Reload from XML'}
        </Button>
      </div>

      {seedResult && (
        <p className="text-sm text-muted-foreground">{seedResult}</p>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {/* Section tabs */}
        <div className="flex flex-wrap gap-1">
          {SPEC_SECTIONS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveSection(key)}
              className={`relative px-3 py-1.5 text-xs rounded-md transition-colors ${
                activeSection === key
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {label}
              {dirtyFields[key] && (
                <span className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-warning" />
              )}
            </button>
          ))}
        </div>

        {SPEC_SECTIONS.map(({ key, hint }) => (
          <div
            key={key}
            className={activeSection === key ? 'flex flex-col gap-1.5' : 'hidden'}
          >
            <p className="text-xs text-muted-foreground">{hint}</p>
            <Textarea
              {...register(key)}
              rows={18}
              className="font-mono text-xs resize-y"
              placeholder={`${key} content…`}
            />
          </div>
        ))}

        <div className="flex items-center gap-3 pt-1">
          {updateSpec.error && (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              {(updateSpec.error as Error).message}
            </p>
          )}
          <div className="ml-auto flex items-center gap-3">
            {isDirty && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => reset(defaultValues)}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Discard
              </Button>
            )}
            <Button type="submit" disabled={!isDirty || updateSpec.isPending}>
              {updateSpec.isPending ? 'Saving…' : 'Save spec'}
            </Button>
            {saved && !isDirty && (
              <span className="flex items-center gap-1 text-sm text-success">
                <CheckCircle2 className="h-4 w-4" />
                Saved
              </span>
            )}
          </div>
        </div>

        {spec?.updated_at && (
          <p className="text-xs text-muted-foreground">
            Last updated {relativeTime(spec.updated_at)}
            {spec.needs_reload && (
              <span className="ml-2 text-warning-foreground">· reload pending</span>
            )}
          </p>
        )}
      </form>
    </div>
  );
}

// ─── Overview tab ─────────────────────────────────────────────────────────────

function OverviewTab({
  data,
  isAI,
}: {
  data: NonNullable<ReturnType<typeof useAgent>['data']>;
  isAI: boolean;
}) {
  const { data: departments } = useDepartments();
  const updateAgent = useUpdateAgent(data.name);
  const unassignSkill = useUnassignSkill();
  const [folderIdDraft, setFolderIdDraft] = useState(data.agent_folder_id ?? '');
  const [folderIdSaved, setFolderIdSaved] = useState(false);
  const [skillDialogOpen, setSkillDialogOpen] = useState(false);

  // data.skills is already scoped to "usable by this agent" (agent_can_use_skill). Individual
  // (Sandman-curated) skills are always this agent's own here and aren't manually removable —
  // shown separately from regular assigned/shared skills.
  const individualSkill = data.skills.find((s) => s.is_individual);
  const ownedSkills = data.skills.filter((s) => !s.is_individual && s.assigned_agents.includes(data.name));
  const globalSkills = data.skills.filter((s) => !s.is_individual && s.assigned_agents.length === 0);
  const assignedIds = new Set(data.skills.map((s) => s.id));

  function handleDepartmentChange(value: string) {
    updateAgent.mutate({ department: value === '__none__' ? null : value });
  }

  function handleRemoveSkill(skillId: string) {
    unassignSkill.mutate({ agentName: data.name, skillId });
  }

  function handleFolderIdSave() {
    updateAgent.mutate(
      { agent_folder_id: folderIdDraft.trim() || null },
      {
        onSuccess: () => {
          setFolderIdSaved(true);
          setTimeout(() => setFolderIdSaved(false), 2000);
        },
      },
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* Humans have no skills — the dispatch log takes the full row instead. */}
      <Card className={isAI ? undefined : 'lg:col-span-2'}>
        <CardHeader>
          <CardTitle>Recent dispatches</CardTitle>
        </CardHeader>
        <CardContent>
          {data.recent_dispatches.length === 0 ? (
            <p className="text-sm text-muted-foreground">None in the last 30 days.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.recent_dispatches.slice(0, 8).map((d) => (
                <li key={d.id} className="flex items-center gap-2">
                  <span className="w-20 shrink-0 text-xs text-muted-foreground">
                    {relativeTime(d.dispatched_at)}
                  </span>
                  <span className="font-medium">{d.from_agent}</span>
                  <span className="text-muted-foreground">→</span>
                  <span className="font-medium">{d.to_agent}</span>
                  <span className="ml-2 flex-1 truncate text-muted-foreground">
                    {d.task_summary}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {isAI && (
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle>Active skills</CardTitle>
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            onClick={() => setSkillDialogOpen(true)}
          >
            <Plus className="h-3.5 w-3.5" />
            Add skill
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Assigned
            </p>
            {ownedSkills.length === 0 && !individualSkill ? (
              <p className="text-sm text-muted-foreground">No skills assigned.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {individualSkill && (
                  <Badge variant="secondary" className="gap-1 text-xs" title="Sandman-curated — not manually removable">
                    {individualSkill.skill_name}
                  </Badge>
                )}
                {ownedSkills.map((s) => (
                  <Badge key={s.id} variant="secondary" className="gap-1 pr-1 text-xs">
                    {s.skill_name}
                    <button
                      type="button"
                      onClick={() => handleRemoveSkill(s.skill_id)}
                      disabled={unassignSkill.isPending}
                      className="rounded-full p-0.5 hover:bg-foreground/10"
                      aria-label={`Remove ${s.skill_name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {globalSkills.length > 0 && (
            <div>
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Shared (all agents)
              </p>
              <div className="flex flex-wrap gap-1.5">
                {globalSkills.map((s) => (
                  <Badge key={s.id} variant="outline" className="text-xs">
                    {s.skill_name}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {isAI && (
        <SkillAssignDialog
          agentName={data.name}
          assignedIds={assignedIds}
          open={skillDialogOpen}
          onOpenChange={setSkillDialogOpen}
        />
      )}

      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>Identity</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Department</dt>
              <dd className="mt-1">
                <Select
                  value={data.department ?? '__none__'}
                  onValueChange={handleDepartmentChange}
                  disabled={updateAgent.isPending}
                >
                  <SelectItem value="__none__">— unassigned —</SelectItem>
                  {departments?.map((d) => (
                    <SelectItem key={d.id} value={d.name}>
                      {d.name}
                    </SelectItem>
                  ))}
                </Select>
              </dd>
            </div>
            <DetailPair label="Status" value={data.status ?? '—'} />
            <DetailPair label="Slack handle" value={data.slack_handle ?? '—'} />
            <DetailPair label="Slack user ID" value={data.slack_user_id ?? '—'} mono />
            <DetailPair label="Last seen" value={data.last_seen_at ? relativeTime(data.last_seen_at) : '—'} />
            {/* Drive folder and webhook are n8n plumbing — meaningless for a human colleague. */}
            {isAI && (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Drive folder ID</dt>
              <dd className="mt-1 flex items-center gap-1.5">
                <Input
                  value={folderIdDraft}
                  onChange={(e) => setFolderIdDraft(e.target.value)}
                  placeholder="Google Drive folder ID"
                  className="h-7 font-mono text-xs"
                  disabled={updateAgent.isPending}
                />
                {folderIdSaved ? (
                  <span className="shrink-0 text-xs text-success">Saved</span>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 shrink-0 px-2 text-xs"
                    disabled={
                      updateAgent.isPending ||
                      folderIdDraft === (data.agent_folder_id ?? '')
                    }
                    onClick={handleFolderIdSave}
                  >
                    Save
                  </Button>
                )}
                {data.agent_folder_id && (
                  <Button asChild size="sm" variant="outline" className="h-7 shrink-0 px-2 text-xs">
                    <a
                      href={`https://drive.google.com/drive/folders/${data.agent_folder_id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <FolderOpen className="h-3.5 w-3.5" />
                      Open
                    </a>
                  </Button>
                )}
              </dd>
            </div>
            )}
            {isAI && data.webhook_url && (
              <div className="col-span-2 sm:col-span-3">
                <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Webhook</dt>
                <dd className="mt-0.5 font-mono text-xs break-all text-muted-foreground">{data.webhook_url}</dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}

function DetailPair({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className={`mt-0.5 ${mono ? 'font-mono text-xs' : 'text-sm'}`}>{value}</dd>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AgentPersonnelPage() {
  const { name } = useParams<{ name: string }>();
  const { data, isLoading } = useAgent(name);
  const { user } = useAuth();
  const updateAgent = useUpdateAgent(name ?? '');
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? 'profile';
  const [specTab, setSpecTab] = useState<'edit' | 'job-description'>('edit');

  const isAdmin = user?.role === 'admin';
  const isActive = data?.status !== 'inactive';

  function handleToggleActive(enabled: boolean) {
    updateAgent.mutate({ status: enabled ? 'active' : 'inactive' });
  }

  if (isLoading) {
    return <div className="text-muted-foreground">Loading {name}…</div>;
  }
  if (!data) {
    return <div className="text-muted-foreground">Agent not found.</div>;
  }

  const isAI = data.type === 'AI';
  const hasSpec = !!data.spec?.role_text?.trim();

  const profile = deriveAgentProfile(data);

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div>
        <Link
          to="/agents"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> All agents
        </Link>
      </div>

      <ProfileHeader
        data={data}
        profile={profile}
        isAI={isAI}
        isAdmin={isAdmin}
        isActive={isActive}
        togglePending={updateAgent.isPending}
        onToggleActive={handleToggleActive}
        onEditAvatar={() => setAvatarDialogOpen(true)}
      />

      <AvatarEditDialog
        agentName={data.name}
        open={avatarDialogOpen}
        onOpenChange={setAvatarDialogOpen}
      />

      <Tabs value={tab} onValueChange={(next) => setSearchParams(next === 'profile' ? {} : { tab: next }, { replace: true })}>
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="overview">Admin</TabsTrigger>
          {isAI && (
            <TabsTrigger value="spec">
              <Pencil className="h-3.5 w-3.5 mr-1.5" />
              Agent spec
            </TabsTrigger>
          )}
          {isAI && (
            <TabsTrigger value="learnings">
              <GraduationCap className="h-3.5 w-3.5 mr-1.5" />
              Learnings
            </TabsTrigger>
          )}
          {isAI && (
            <TabsTrigger value="stats">
              <BarChart3 className="h-3.5 w-3.5 mr-1.5" />
              Stats
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <ProfileTab data={data} profile={profile} />
        </TabsContent>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab data={data} isAI={isAI} />
        </TabsContent>

        {isAI && (
          <TabsContent value="spec" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Agent spec editor</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Each section maps to a block in the agent's{' '}
                  <code className="text-xs bg-muted px-1 rounded">job_description.xml</code>.
                  Changes are saved to the DB and trigger a reload on next agent run.
                </p>
                {/* Sub-nav: Edit / Job Description */}
                <div className="flex gap-1 mt-3 border-b border-border pb-1">
                  <button
                    type="button"
                    onClick={() => setSpecTab('edit')}
                    className={`px-3 py-1.5 text-xs rounded-t-md transition-colors ${
                      specTab === 'edit'
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    Edit sections
                  </button>
                  <button
                    type="button"
                    onClick={() => setSpecTab('job-description')}
                    className={`px-3 py-1.5 text-xs rounded-t-md transition-colors ${
                      specTab === 'job-description'
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    Job description
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                {specTab === 'edit' ? (
                  <SpecEditor agentName={data.name} spec={data.spec} />
                ) : (
                  <JobDescriptionTab agentName={data.name} hasSpec={hasSpec} />
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {isAI && (
          <TabsContent value="learnings" className="mt-6">
            <AgentLearningsTab agentName={data.name} />
          </TabsContent>
        )}

        {isAI && (
          <TabsContent value="stats" className="mt-6">
            <AgentStatsTab agentName={data.name} recentRuns={data.recent_runs} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
